import { useState, useEffect } from 'react';
import { Button, Row, Col, Dropdown } from 'react-bootstrap';
import { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment';
import CardPayment from './CardPayment';
import { CardTransaction } from './types';
import { getCardImageByName } from './PaymentUtil';
import { currencyFormat, extractTimestamp } from '../../util/general';
import { modal } from '../modal/ModalEmitter';
import PaymentCardRefundForm from './PaymentCardRefundForm';
import toastr from '../toastr';

interface Props {
  maxRefundAmount?: number;
  onRefund?: ({ saleTx, refundTx }: { saleTx: CardTransaction; refundTx: CardTransaction }) => void;
  onSale?: (saleTx: CardTransaction) => void;
  onVoid?: ({ saleTx, voidTx }: { saleTx: CardTransaction; voidTx: CardTransaction }) => void;
  paymentTransaction: CardTransaction;
  readOnly?: boolean;
  sessionId?: string;
  showRefundForm?: boolean;
  userInitials: string;
}

const PaymentCardTransaction = ({
  maxRefundAmount,
  onRefund,
  onSale,
  onVoid,
  paymentTransaction,
  readOnly = false,
  sessionId,
  showRefundForm = false,
  userInitials,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState<CardPayment>(new CardPayment(paymentTransaction));
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (payment.transactionResult !== 'approved') {
      setMessage(`${payment.transactionResult}: ${payment.transactionResponse}`);
    }
  }, [payment]);

  const doVoid = ({ saleTx, voidTx }: { saleTx: CardTransaction; voidTx: CardTransaction }) => {
    if (voidTx.transactionResult !== 'approved') {
      setMessage(`Void ${voidTx.transactionResult} ${voidTx.transactionResponse}`);
    } else {
      setPayment(new CardPayment(saleTx));
      if (onVoid) {
        onVoid({ saleTx, voidTx });
      }
    }
  };

  const doRefund = ({ refundTx, saleTx }: { refundTx: CardTransaction; saleTx: CardTransaction }) => {
    if (refundTx.transactionResult !== 'approved') {
      setMessage(`Refund ${refundTx.transactionResult} ${refundTx.transactionResponse}`);
    } else {
      setPayment(new CardPayment(saleTx));
      if (onRefund) {
        onRefund({ saleTx, refundTx });
      }
    }
  };

  const doSale = (saleTx: CardTransaction) => {
    if (saleTx.transactionResult !== 'approved') {
      setMessage(`Sale ${saleTx.transactionResult} ${saleTx.transactionResponse}`);
    } else {
      setPayment(new CardPayment(saleTx));
      if (onSale) {
        onSale(saleTx);
      }
    }
  };

  const handleStatusRequest = () => {
    setLoading(true);
    payment
      .doStatus()
      .then((response: AxiosResponse) => {
        const txType = payment.transactionType;
        if (txType === 'void') {
          doVoid({ saleTx: response.data.sale, voidTx: response.data.void });
        } else if (txType === 'refund') {
          doRefund({ saleTx: response.data.sale, refundTx: response.data.refund });
        } else if (txType === 'sale') {
          doSale(response.data.sale);
        } else {
          console.error('Unhandled transaction type:', txType);
        }
      })
      .catch((err: Error | AxiosError) => {
        toastr.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleVoidRequest = () => {
    setLoading(true);
    payment
      .doVoidOrRefundPayment({ userInitials, sessionId })
      .then((response: AxiosResponse | undefined) => {
        if (!response) {
          // it wasn't voidable or refundable
          return;
        }

        const { sale, refund } = response.data;
        if (response.data.void) {
          doVoid({ saleTx: sale, voidTx: response.data.void });
        } else if (refund) {
          doRefund({ refundTx: refund, saleTx: sale });
        }
      })
      .catch((err: Error | AxiosError) => {
        // TODO handle error
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  const handleRefund = ({
    refundTx,
    saleTx,
    voidTx,
  }: {
    refundTx?: CardTransaction;
    saleTx: CardTransaction;
    voidTx?: CardTransaction;
  }) => {
    if (refundTx) {
      doRefund({ refundTx, saleTx });
    } else if (voidTx) {
      doVoid({ saleTx, voidTx });
    }
  };

  const handleRefundError = (err: Error) => {
    toastr.error(err.message);
  };

  const openRefundForm = () => {
    modal.open({
      title: 'Refund Card',
      size: 'md',
      hideFooter: true,
      hidePrint: true,
      style: 'center',
      staticBackdrop: true,
      headerClass: 'primary',
      message: (
        <>
          <PaymentCardRefundForm
            maxAmount={parseFloat((payment.amount - payment.getRefundedAmount()).toFixed(2))}
            onError={err => {
              handleRefundError(err);
              modal.close();
            }}
            onRefund={response => {
              handleRefund(response);
              modal.close();
            }}
            paymentId={payment.id}
            userInitials={userInitials}
            sessionId={sessionId}
          />
        </>
      ),
    });
  };

  const renderAmount = () => {
    if (payment.isVoid() || payment.isFullyRefunded()) {
      return <s>{currencyFormat(payment.amount)}</s>;
    }

    if (payment.hasRefunds()) {
      return currencyFormat(payment.amount - payment.getRefundedAmount());
    }

    return currencyFormat(payment.amount);
  };

  const renderResult = () => {
    if (payment.isFullyRefunded() || payment.isVoid()) {
      return <span className="text-danger text-bold">REFUNDED</span>;
    }

    return payment.transactionResult;
  };

  const renderActions = () => {
    if (readOnly) {
      return null;
    }

    if (loading) {
      return (
        <Button variant="default" disabled>
          <i className="fas fa-circle-notch fa-spin" /> &nbsp; Processing
        </Button>
      );
    }

    if (payment.transactionResult === 'started') {
      return (
        <Button variant="default" onClick={handleStatusRequest}>
          Status
        </Button>
      );
    }

    if (!showRefundForm && payment.canRefund()) {
      return (
        <Dropdown className="no-caret" alignRight>
          <Dropdown.Toggle variant="secondary">
            <i className="fas fa-cog" />
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {!payment.isVoid() && (
              <>
                {!payment.isFullyRefunded() && (
                  <Dropdown.Item onClick={openRefundForm} className="text-danger text-bold">
                    Issue Refund
                  </Dropdown.Item>
                )}
                {payment.canVoid() && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleVoidRequest} className="text-danger text-bold">
                      Void (Full Refund)
                    </Dropdown.Item>
                  </>
                )}
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      );
    }

    return null;
  };

  const renderRefunds = () => {
    if (!payment.hasRefunds() || !payment.refunds) {
      return null;
    }

    return (
      <Row>
        <Col>
          {payment.refunds.map((refund, i) => {
            return (
              <div key={i}>
                {moment(extractTimestamp(refund.id)).format('M/D/YY')} Refund <i className="fas fa-arrow-right" />{' '}
                {currencyFormat(refund.amount)}
                {!!refund.invoiceNum && <> ({refund.invoiceNum})</>}
              </div>
            );
          })}
        </Col>
      </Row>
    );
  };

  const renderVoid = () => {
    if (!payment.isVoid()) {
      return null;
    }

    return (
      <Row>
        <Col>
          <div>
            {moment(extractTimestamp(payment.voidId)).format('M/D/YY')} Void <i className="fas fa-arrow-right" />{' '}
            {currencyFormat(payment.amount)}
          </div>
        </Col>
      </Row>
    );
  };

  const getMaxRefundAmount = () => {
    const refundAbleAmount = parseFloat((payment.amount - payment.getRefundedAmount()).toFixed(2));
    if (maxRefundAmount !== undefined) {
      return Math.min(refundAbleAmount, maxRefundAmount);
    } else {
      return refundAbleAmount;
    }
  };

  return (
    <>
      <Row>
        <Col xs="auto">
          <div
            style={{ border: '1px solid #AAA', fontFamily: 'Roboto Mono', backgroundColor: '#EEE' }}
            className="rounded"
          >
            <img src={getCardImageByName(payment.cardType)} style={{ width: '50px' }} />
            <div className="d-inline-block pl-1 pr-1 font-weight-bold">{payment.cardLast4}</div>
          </div>
        </Col>
        <Col>
          <div className="d-inline-block" style={{ fontSize: '19px' }}>
            <span className="text-bold mr-2">{renderAmount()}</span>
            <span className="text-uppercase">{renderResult()}</span>
          </div>
        </Col>
        <Col xs="auto">{renderActions()}</Col>
      </Row>
      {renderRefunds()}
      {renderVoid()}
      {showRefundForm && !payment.isFullyRefunded() && !payment.isVoid() && getMaxRefundAmount() > 0 && (
        <>
          <hr />
          <PaymentCardRefundForm
            defaultAmount={getMaxRefundAmount()}
            maxAmount={getMaxRefundAmount()}
            onError={handleRefundError}
            onRefund={handleRefund}
            paymentId={payment.id}
            sessionId={sessionId}
            userInitials={userInitials}
          />
        </>
      )}

      {!!message && (
        <Row>
          <Col>
            <div className="text-uppercase text-danger text-bold">{message}</div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default PaymentCardTransaction;
